import { BaseControl } from '@Common/Controls/BaseControl';
import { ContextMenu } from '@Common/Controls/ContextMenu';
import { DragAndDrop } from '@Common/Controls/DragAndDrop';
import constants from '@Common/Core/Constant';
import { IContextMenuItem } from '@Common/Core/ContextMenuItems';
import { destroyEvents, safeDestroys } from '@Common/Core/Destroyable';
import { properties } from '@Common/Core/Properties';
import { AlignType, ExecuteAction, MenuItemSpacing, MenuItemType } from '@Common/Enums/Enum';

import * as commonUtils from '@Common/Helpers/CommonUtils';
import pointerHelper, { hoverHelper } from '@Common/Helpers/PointerHelper';
import urlHelper from '@Common/Helpers/UrlHelper';
import { SearchMenuItemControl } from '@View/Components/Menus/SearchMenuItemControl';
import { Scroll } from '@View/Core/Scroll';
import { TreeStore } from './TreeStore';

export interface IBaseMenuNode {
    hasChildren: boolean;
    data: any;
    control: JQuery;
    level: number;
    children: any[];

    select(b);

    deselect();

    deselectData();

    check(isChecked: boolean);

    remove();

    add(newItem: any): IBaseMenuNode;

    insert(index: number, newItem: any): IBaseMenuNode;

    getNodes(): IBaseMenuNode[];

    getParent(): any;

    getParentNode(): IBaseMenuNode;

    clear();

    expanded: boolean;

    expand();

    collapse();

    onExpand(handler: () => void);

    onCollapse(handler: () => void);

    update(newItem: any): void;

    isVisible(): boolean;

    addTitle();

    removeTitle();

    hideArrow();

    showArrow();

    getChildren(): any[];

    getLiContent(): JQuery;

    getData(): any;

    changeLevelStyle(levelStyles, defaultStyles);

    getLevelStyle(): any;

    getFullNameWidth(): number;

    parent: IBaseMenuNode;

    visibility(state: boolean): void;

    checked: boolean;

    setContentCss(css: any);
}

export const selectMode = {
    simple: 'simple',
    multiple: 'multiple'
};

/*
    Base menu param

    css: inline css style, will be apply on root
    className: class style, will be apply on root
    width: width in px, percent or number, will be implemet for root
    height: height in px, percent or number, will be implemet for root
    validator: will be use in baseMenu.validator funciton
    isDesktop: set dekctop or tablet type
    isVertical: use "true" for vertical menu or list
    imageServiceUrl: getting image from service
    selectable: if "true" then baseMenu can suport select mode,
        that means: user can select items and all selectable event should fire
    selectOnContextMenu: if "true", then node will be selected if user open CM on node
    checkable: if "true" then baseMenu should have a checkbox, and user can check/uncheck node,
        also all checkable event should run
    checkBoxVisibilityComparer: user can hide checkbox for some node
    openParentOnCheck: if "true" and user programmatically check node and node has a parent, then parent node
        will be opened
    checkOnClick: if "true", node will be checked if user click on node content
    selectOnCheck: if "true", node will be selected uf user check node
    keepCheckoffHierarchy: if "true" and user check some node, then all parent node chould be checked
    unselectOnClick: if "true", then node will be unselect on click
    selectMode: should be selected from "com.altosoft.controls.selectMode",
        if simple - user can select only one node can be selected in the same time
        if multiple - user can select multiple node using Ctrl and Shift button
    isSimpleCheckMode: if "true", only one node can be checked in the same time
    levelStyle: array style for tree control,
        level starting from 0,
        each style should have an object:
            {
                itemDefaultStyle: 'styleName'
                itemHoverStyle: 'styleName'
                itemSelectedStyle: 'styleName'
            }
    defaultLevelStyle: if param.levelStyle is null or param.levelStyle is null for some level,
        then level style getting from param.defaultLevelStyle
    highlightAllSelectedWay: if "true", then all paren node will be selected for selected node,
        using in menu component
    comparer: this compare will use when need to compare base menu item,
        when select or check some item,
        it should be a function which return -1, 1 or 0,
        by default - "item.id" will be compare
    allowContextMenu: allow use CM for node, using getContextMenuItems function for filling CM
    canDrag: if "true", all items can be draggable
    canDrop: if "true" all items can be droppable
    allowSorting: if "true", then user can sort item in same level
    allowNestedSorting: if "true", then user can sort item in different level
    allowKeyBoardNavigation: if "true", allow navigation in base menu using arrow,
    allowKeySearch: if "true", then base menu will scroll to item which starting with char which user click
    sortable: if "true", items will be sort by name
    folderOnTop: if "true", then folder node will be on the top position
    visibilityComparer: if not null, then it will be use for hide/show iten in the base menu
    openByClick: if true then node will be collpsed by body click,
        if false open node will be possible only by icon click
    idPropertyName: if not null, then it will be use like a item id (item[param.idPropertyName]),
    namePropertyName: if not null, then it will be use like a item name (item[param.namePropertyName]),
*/
type GenericAction = (...args: any[]) => void;

export class BaseMenu extends BaseControl {
    private _onCollapse: GenericAction = $.noop;
    private _onExpand: GenericAction = $.noop;
    private _onItemClick: GenericAction = $.noop;
    private _onItemRightClick: GenericAction = $.noop;
    private _onItemCheck: GenericAction = $.noop;
    private _onItemDoubleClick: GenericAction = $.noop;
    private _onSelectionChanged: GenericAction = $.noop;
    private _onCheckedChanged: GenericAction = $.noop;
    private _onScrollMove: GenericAction = $.noop;
    private _onValidate: GenericAction = $.noop;
    private _onContextMenuItemClick: GenericAction = $.noop;
    private _onAppendItem: GenericAction = $.noop;

    private _onDrag: GenericAction = $.noop;
    private _onDragStart: GenericAction = $.noop;
    private _onDragStop: GenericAction = $.noop;
    private _onDragOver: GenericAction = $.noop;
    private _onDragOut: GenericAction = $.noop;

    private _onDrop: ICallback<any> = $.noop;
    private _onDropOver: GenericAction = $.noop;

    private _onPreDrag: GenericAction = $.noop;
    private _onScrolledToEnd: GenericAction = $.noop;

    private _onSort: (a, b, c, d) => void = $.noop;
    private _onSorted: (a) => void = $.noop;

    private _beforeExpand: GenericAction = $.noop;
    protected _rootUl: any;
    private _emptyMessageBlock: any;
    private _items: any[];
    private _foundItems: any[];
    private _childrens: any[];
    private _checkedItemsForEmpty: any[];
    private _openedNodes: any;
    private _levelStyles: any[];
    protected _body: JQuery<HTMLElement>;
    private _maxVisibleCount: number;
    private _visibleItemsCount: number;
    private _dragAndDropSort: DragAndDrop;
    protected _treeStore: any;
    private _dropControls: any[];
    private _sortIsBefore: any;

    scroll: any;
    draggingContainer: any;
    tabIndex: number;
    paramisTree: any;
    currentItem: any;
    currentSelectedItems: any[];
    currentCheckedItems: any[];
    current: any;

    constructor(param) {
        super(
            $.extend(
                {
                    css: null,
                    className: '',
                    width: '100px',
                    height: '100px',

                    align: null,
                    itemsAlign: AlignType.left,

                    // validator
                    validator: null,

                    // is desktop
                    isDesktop: properties.isDesktopBrowser,
                    showContainerIndicator: false,

                    // vertical menu
                    isVertical: true,

                    // image service
                    imageServiceUrl: null,

                    // selectable items
                    selectable: true,
                    selectOnContextMenu: true,

                    // checkable items
                    checkable: false,
                    checkBoxVisibilityComparer: null,
                    openParentOnCheck: false,
                    //checkOnSelect: false,
                    checkOnClick: false,
                    selectOnCheck: false,
                    keepCheckoffHierarchy: true,
                    unselectOnClick: false,

                    // selection
                    selectMode: selectMode.simple,

                    // check
                    isSimpleCheckMode: false,

                    // style
                    defaultContentStyle: 'driver-content',

                    // level styles
                    levelStyle: [
                        {
                            itemDefaultStyle: constants.driverDefaultStyle.itemDefaultStyle,
                            itemHoverStyle: constants.driverDefaultStyle.itemHoverStyle,
                            itemSelectedStyle: constants.driverDefaultStyle.itemSelectedStyle,
                            itemOpenedStyle: constants.driverDefaultStyle.itemOpenedStyle,

                            itemDefaultBorderStyle: constants.driverDefaultStyle.itemDefaultBorderStyle,
                            itemHoverBorderStyle: constants.driverDefaultStyle.itemHoverBorderStyle,
                            itemSelectedBorderStyle: constants.driverDefaultStyle.itemSelectedBorderStyle,
                            itemOpenedBorderStyle: constants.driverDefaultStyle.itemOpenedBorderStyle
                        }
                    ],

                    // default styles
                    defaultLevelStyle: {
                        itemDefaultStyle: constants.driverDefaultStyle.itemDefaultStyle,
                        itemHoverStyle: constants.driverDefaultStyle.itemHoverStyle,
                        itemSelectedStyle: constants.driverDefaultStyle.itemSelectedStyle,
                        itemOpenedStyle: constants.driverDefaultStyle.itemOpenedStyle,

                        itemDefaultBorderStyle: constants.driverDefaultStyle.itemDefaultBorderStyle,
                        itemHoverBorderStyle: constants.driverDefaultStyle.itemHoverBorderStyle,
                        itemSelectedBorderStyle: constants.driverDefaultStyle.itemSelectedBorderStyle,
                        itemOpenedBorderStyle: constants.driverDefaultStyle.itemOpenedBorderStyle
                    },

                    contentStyle: constants.driverDefaultStyle.contentStyle,

                    // select full element way when select
                    highlightAllSelectedWay: false,

                    // selectors comparer
                    comparer: undefined,

                    // CM
                    allowContextMenu: false,

                    // drag/drop
                    canDrag: false,
                    canDrop: false,
                    allowSorting: false,
                    allowNestedSorting: false,
                    greedy: false,

                    // keyboard navigation
                    allowKeyBoardNavigation: true,
                    allowKeySearch: false,

                    contentPadding: 4,
                    iconWidth: 20,

                    sortable: true,
                    folderOnTop: true,

                    itemSpacingType: MenuItemSpacing.auto,
                    itemSpacing: '',

                    visibilityComparer: null,

                    // if true then node will be collpsed by body click,
                    // if false open node will be possible only by icon click
                    openByClick: true,

                    hScroll: false,
                    vScroll: true,

                    idPropertyName: 'id',
                    namePropertyName: 'name',

                    lazyLoadChildren: false,
                    lazyLoad: false,
                    scrollStepPackage: 50,

                    cloneItems: false,

                    sort: null,

                    addItemsUntillScroll: false,

                    possibleRightAlign: false
                },
                param
            )
        );

        this._root = null;
        this._rootUl = null;
        this._emptyMessageBlock = null;

        this._items = [];
        this._foundItems = [];
        this._childrens = [];
        this._checkedItemsForEmpty = [];
        this._openedNodes = null;
        this._levelStyles = [];

        this._body = $('body');
        this.scroll = null;
        this.draggingContainer = null;
        this.tabIndex = -1;
        this._maxVisibleCount = 0;
        this._visibleItemsCount = 0;

        this._dragAndDrop = new DragAndDrop(
            $.extend(this.param, {
                refreshPositions: true,
                scrollArea: this._root
            })
        );

        this._dragAndDropSort = new DragAndDrop({
            canDrop: true,
            canDrag: true,
            scopes: ['base-menu-sorting']
        });
        this._treeStore = new TreeStore(this.param);
        this._dropControls = [];

        this._treeStore.onSelectionChanged(
            function (a, b, c) {
                this._onSelectionChanged(a, b, c);
            }.bind(this)
        );
        this._treeStore.onCheckedChanged(
            function (a, b) {
                this._onCheckedChanged(a, b);
            }.bind(this)
        );
        this._treeStore.onAdded(
            function (node, index) {
                this._createItem(node);

                if (index == this._treeStore.items().length - 1) {
                    if (node.control && node.control.li) {
                        this._rootUl.append(node.control.li);
                    }
                } else {
                    const li = this._rootUl.children('li:eq(' + index + ')');
                    if (li.length > 0) {
                        node.control.li.insertBefore(li);
                    }
                }
            }.bind(this)
        );

        this._init();
    }

    protected get param() {
        return this._param;
    }

    protected _init() {
        const root = document.createElement('div');
        root.className = `base-menu ${this.param.contentStyle} ${this.param.defaultContentStyle}`;
        root.classList.add(this.param.isVertical === true ? 'vertical' : 'horizontal');
        if (this.param.checkable === true) {
            root.classList.add('checkable');
        }
        if (!this.param.isDesktop) {
            root.classList.add('mobile');
        }

        this._root = $(root);

        if (this.param.allowContextMenu) {
            this._root.contextMenuAction(
                function (coordinates) {
                    const menuItems = _.map(this.getContextMenuItems(), function (menuItem) {
                        return { item: menuItem, checked: false };
                    });

                    const contextMenu = ContextMenu(coordinates);
                    contextMenu.setValue(menuItems);
                    contextMenu.itemClick(
                        function (cmItem) {
                            this._onContextMenuItemClick(cmItem);
                        }.bind(this)
                    );
                }.bind(this)
            );
        }

        this._rootUl = $(document.createElement('ol')).appendTo(this._root);
        this.param.isVertical === true && this._rootUl.addClass('vertical');

        this._applyDefaultParam();

        // message for empty list
        if (this.param.emptyMessage) {
            this._emptyMessageBlock = $('<div class="empty-message"/>').prependTo(this._root);

            // if jQuery obj
            if (this.param.emptyMessage instanceof jQuery) {
                this._emptyMessageBlock.append(this.param.emptyMessage);
            }

            // if controls
            else if (this.param.emptyMessage.getControl) {
                this._emptyMessageBlock.append(this.param.emptyMessage.getControl());
            }

            // if simple html
            else {
                this._emptyMessageBlock.html(this.param.emptyMessage);
            }
        }
    }

    _sortItem(prop) {
        return function (a, b) {
            if (!a[prop] || !b[prop]) {
                return 0;
            }

            const A = a[prop].toLowerCase();
            const B = b[prop].toLowerCase();

            if (A < B) {
                return -1;
            } else if (A > B) {
                return 1;
            } else {
                return 0;
            }
        };
    }

    _createIScroll() {
        this.scroll = new Scroll(this._root, {
            rightPadding: false,
            scrollX: this.param.hScroll,
            scrollY: this.param.vScroll,
            onScrolledToEnd: function () {
                // client lazy load
                if (this._items.length != this._treeStore.items().length) {
                    if (this._foundItems.length && this._visibleItemsCount.length != this._foundItems.length) {
                        const appendItems = this._foundItems.slice(this._visibleItemsCount, this._visibleItemsCount + this.param.scrollStepPackage);
                        this._visibleItemsCount += appendItems.length;
                        this._insertItems(appendItems, { needSetvisibility: false, needConcat: false });
                    } else if (this.param.lazyLoad && this._visibleItemsCount != this._items.length) {
                        const appendItems = this._items.slice(this._visibleItemsCount, this._visibleItemsCount + this.param.scrollStepPackage);
                        this._visibleItemsCount += appendItems.length;
                        this._insertItems(appendItems, { needSetvisibility: false, needConcat: false });
                    }

                    if (this._maxVisibleCount < this._visibleItemsCount) {
                        this._maxVisibleCount = this._visibleItemsCount;
                    }
                }
                this._onScrolledToEnd();
            }.bind(this),
            onScrollMove: this._onScrollMove
        });

        this._dragAndDrop.setScrollArea(this._root);
        this._dragAndDrop.setIScroll(this.scroll);
    }

    _insertItems(items, param) {
        this._treeStore.addItems(items, { isPrepend: param.isPrepend });

        if (this.param.lazyLoad && (!param || param.needConcat !== false)) {
            Array.prototype[param.isPrepend ? 'unshift' : 'push'].apply(this._items, items);
        }

        if (this.param.sortable) {
            const nodes = this._treeStore.nodes();

            _(nodes).forEach(
                function (i, index) {
                    if (!i.control) {
                        this._createItem(i);

                        const node = nodes[index - 1];
                        if (node && node.control) {
                            i.control && i.control.li.insertAfter(node.control.li);
                        } else {
                            i.control && i.control.li.prependTo(this._rootUl);
                        }
                    }
                }.bind(this)
            );
        } else {
            if (param.isPrepend) {
                _(this._treeStore.nodes())
                    .take(items.length)
                    .reverse()
                    .forEach(
                        function (i) {
                            this._createItem(i);
                            i.control && i.control.li.prependTo(this._rootUl);
                        }.bind(this)
                    );
            } else {
                _(this._treeStore.nodes())
                    .takeRight(items.length)
                    .forEach(
                        function (i) {
                            this._createItem(i);
                            i.control && i.control.li.appendTo(this._rootUl);
                        }.bind(this)
                    );
            }
        }

        this.adjust();

        if (!param || param.needSetvisibility !== false) {
            this.setNodesVisibility();
        }

        if (!param || param.needSetCheckBoxVisibility !== false) {
            this.setCheckBoxVisibility();
        }
    }

    // eslint-disable-next-line complexity
    _createItem(node) {
        //window.items++;
        const item = node.data;
        const param = node.param;

        // base menu control
        /*node.control = {
            li: parent li,
            childrenUl: ul element for keeping childrens li,
            liContent: content for items,
            name: name of the item
            levelStyle: levelStyle,
            check: function(),
            select: function()
        }*/
        if (!node.control) {
            node.control = {};
        }
        node.control.levelStyle = node.data.specificStyles || this.getLevelStyle(param.level, this.param.levelStyle, this.param.defaultLevelStyle);

        node.control.setLevelStyles = function (levelStyles, defaultStyles) {
            const liContent = node.control.liContent;
            const newLevelStyle = this.getLevelStyle(node.level, levelStyles, defaultStyles);

            //default
            if (liContent.hasClass(node.control.levelStyle.itemDefaultStyle)) {
                liContent.removeClass(node.control.levelStyle.itemDefaultStyle);
                liContent.addClass(newLevelStyle.itemDefaultStyle);
            }
            if (liContent.hasClass(node.control.levelStyle.itemDefaultBorderStyle)) {
                liContent.removeClass(node.control.levelStyle.itemDefaultBorderStyle);
                liContent.addClass(newLevelStyle.itemDefaultBorderStyle);
            }
            //select
            if (liContent.hasClass(node.control.levelStyle.itemSelectedStyle)) {
                liContent.removeClass(node.control.levelStyle.itemSelectedStyle);
                liContent.addClass(newLevelStyle.itemSelectedStyle);
            }
            if (liContent.hasClass(node.control.levelStyle.itemSelectedBorderStyle)) {
                liContent.removeClass(node.control.levelStyle.itemSelectedBorderStyle);
                liContent.addClass(newLevelStyle.itemSelectedBorderStyle);
            }
            //hover
            if (liContent.hasClass(node.control.levelStyle.itemHoverStyle)) {
                liContent.removeClass(node.control.levelStyle.itemHoverStyle);
                liContent.addClass(newLevelStyle.itemHoverStyle);
            }
            if (liContent.hasClass(node.control.levelStyle.itemHoverBorderStyle)) {
                liContent.removeClass(node.control.levelStyle.itemHoverBorderStyle);
                liContent.addClass(newLevelStyle.itemHoverBorderStyle);
            }
            //opened
            if (liContent.hasClass(node.control.levelStyle.itemOpenedStyle)) {
                liContent.removeClass(node.control.levelStyle.itemOpenedStyle);
                liContent.addClass(newLevelStyle.itemOpenedStyle);
            }
            if (liContent.hasClass(node.control.levelStyle.itemOpenedBorderStyle)) {
                liContent.removeClass(node.control.levelStyle.itemOpenedBorderStyle);
                liContent.addClass(newLevelStyle.itemOpenedBorderStyle);
            }

            node.control.levelStyle = newLevelStyle;
        }.bind(this);

        node.setContentCss = function (css) {
            node.control.liContent.css(css);
        };

        node.control.toggleContentStyle = function (style, apply) {
            node.control.liContent.toggleClass(style, apply);
        };

        node.control.update = function (data, updateParam) {
            this._createItem(node);

            if (node.param.level == 0) {
                let index = 0;
                _(this._treeStore.nodes()).forEach(function (i, ind) {
                    if (i == node) {
                        index = ind;
                        return false;
                    }
                });

                if (index == 0) {
                    node.control.li.prependTo(this._rootUl);
                } else {
                    if (index < this._treeStore.items().length / 2) {
                        node.control.li.insertAfter(this._rootUl.children('li').eq(index - 1));
                    } else {
                        node.control.li.insertAfter(this._rootUl.children('li').children('li').eq(index));
                    }
                }
            } else {
                let index = 0;
                _(node.parent.children).forEach(function (i, ind) {
                    if (i == node) {
                        index = ind;
                        return false;
                    }
                });

                if (index == 0) {
                    node.control.li.prependTo(node.parent.control.childrenUl);
                } else {
                    if (index < node.parent.children.length / 2) {
                        node.control.li.insertAfter(node.parent.control.childrenUl.children('li').eq(index - 1));
                    } else {
                        node.control.li.insertAfter(node.parent.control.childrenUl.children('li').eq(index));
                    }
                }
            }

            this._treeStore.setNodesVisibility();
            this._treeStore.setCheckBoxVisibility();
        }.bind(this);
        node.control.remove = function () {
            if (node.parent) {
                const hasChildren = node.parent.data.children && node.parent.data.children.length > 0;
                node.parent.control.li.toggleClass('show-arrow', hasChildren);
            }

            node.control.li.remove();
        };
        node.control.check = function (data, checkParam) {
            if (node.control.checkbox) {
                node.control.checkbox.toggleClass('on', data === true);
            }
            this._onItemCheck(node.data, checkParam);
        }.bind(this);
        node.control.select = function (data) {
            if (node.control && node.control.liContent) {
                data === true ? node.control.setSelectedStyle() : node.control.setDefaultStyle();
            }

            function selectParent(parentNode) {
                if (parentNode.control && parentNode.control.liContent) {
                    parentNode.control.setSelectedStyle();
                    parentNode.control.selected = true;

                    parentNode.parent && selectParent(parentNode.parent);
                }
            }

            if (data === true && this.param.highlightAllSelectedWay) {
                this._treeStore.forEachNode(function (i) {
                    if (i.control) {
                        i.control.selected && i.control.setDefaultStyle();
                        i.control.selected = false;
                    }
                });
                node.parent && selectParent(node.parent);
            }
        }.bind(this);

        node.control.showArrow = function () {
            node.control.li.addClass('show-arrow');
        };
        node.control.hideArrow = function () {
            node.control.li.removeClass('show-arrow');
        };
        node.control.expand = function (param, ready) {
            if (this._beforeExpand(node)) {
                return;
            }

            if (!node.hasChildren && item.dynamic && !item.dinamicIsLoaded) {
                this._loadDynamicItems(node, ready);
            } else if (this.param.lazyLoadChildren) {
                if (!node.control.childrenUl) {
                    this.addChildrens(node);
                } else if (node.control.childrenUl && !node.control.childrenUl.elementInDOM()) {
                    this.addChildrens(node);
                    node.control.childrenUl.appendTo(node.control.li);
                }

                this._onExpand(node.data);
                node.control.li.addClass('opened');
                (!param || param.needAdjust) && this.adjust();
            } else {
                this._onExpand(node.data);
                node.control.li.addClass('opened');
                (!param || param.needAdjust) && this.adjust();
            }

            // bind drag and drop
            if (node.hasChildren && this.param.canDrag) {
                _(node.children).forEach(
                    function (i) {
                        i.data.disableDrag !== true && !i.control.dragBinded && this.bindDraggable(i);
                    }.bind(this)
                );
            }

            if (node.hasChildren && this.param.canDrop) {
                _(node.children).forEach(
                    function (i) {
                        !i.control.dropBinded && this.bindDroppable(i);
                    }.bind(this)
                );
            }
        }.bind(this);
        node.control.collapse = function (param) {
            this._onCollapse(node.data);
            node.control.li.removeClass('opened');
            (!param || param.needAdjust) && this.adjust();
        }.bind(this);

        node.onVisibilityChanges = function () {
            node.control.li.toggleClass('hidden', node.visibility() === false);
        };

        node.onCheckableChanges = function () {
            node.control.liContent.find('> .checkbox').toggleClass('disabled', node.checkable() === false);
        };

        const rightTextAlign = 'right-text-align';
        node.control.setSelectedStyle = function () {
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedBorderStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemSelectedStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemSelectedBorderStyle);

            node.control.liContent.addClass('selected');

            this._param.possibleRightAlign && node.control.liContent.toggleClass(rightTextAlign, node.control.liContent.css('text-align') == 'right');
        }.bind(this);

        node.control.setHoverStyle = function () {
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedBorderStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemHoverStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemHoverBorderStyle);

            this._param.possibleRightAlign && node.control.liContent.toggleClass(rightTextAlign, node.control.liContent.css('text-align') == 'right');
        }.bind(this);

        node.control.setDefaultStyle = function () {
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemOpenedBorderStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemDefaultStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemDefaultBorderStyle);

            node.control.liContent.removeClass('selected');

            this._param.possibleRightAlign && node.control.liContent.toggleClass(rightTextAlign, node.control.liContent.css('text-align') == 'right');
        }.bind(this);

        node.control.setOpenedStyle = function () {
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemDefaultBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemHoverBorderStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedStyle);
            node.control.liContent.removeClass(node.control.levelStyle.itemSelectedBorderStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemOpenedStyle);
            node.control.liContent.addClass(node.control.levelStyle.itemOpenedBorderStyle);

            this._param.possibleRightAlign && node.control.liContent.toggleClass(rightTextAlign, node.control.liContent.css('text-align') == 'right');
        }.bind(this);

        node.onSelect = node.control.select;
        node.onCheck = node.control.check;
        node.onRemove = node.control.remove;
        node.onUpdate = node.control.update;
        node.onExpand(node.control.expand);
        node.onCollapse(node.control.collapse);
        node.onShowChildren(node.control.showArrow);
        node.onHideChildren(node.control.hideArrow);

        // if separator
        if (item.separator || item.menuItemType == MenuItemType.separator) {
            //TODO: Rework li color
            const sep = $('<li class="separator"><div></div></li>').addClass(node.control.levelStyle.itemDefaultStyle).appendTo(this._body);

            sep.find('div').css('backgroundColor', sep.css('color'));
            sep.detach();
            node.control.li = sep;
            return;
        }

        // if search item
        if (item.menuItemType == MenuItemType.search) {
            node.control.search = new SearchMenuItemControl(item);
            node.control.li = node.control.search.getControl();
            return;
        }

        // li
        if (node.control && node.control.li) {
            node.control.li.removeClass();
            node.control.li.unbind();
            node.control.li.empty();
        } else {
            node.control.li = $(document.createElement('li'));
        }

        (node.expanded || (node.hasChildren && item.isExpandedFolder == true)) === true && node.control.li.addClass('opened');
        if (item.disable !== undefined) {
            node.control.li.toggleClass('disable disabled', item.disable);
        }
        if (item.visibility !== undefined) {
            node.control.li.toggleClass('hidden', !item.visibility);
        }

        (node.data.isFolder !== undefined ? node.data.isFolder : node.data.children && node.data.children.length > 0) &&
            node.control.li.addClass('show-arrow');

        // set item width if itemSpacingType is specific
        if (
            node.param.level == 0 &&
            (this.param.itemSpacingType == MenuItemSpacing.specific || this.param.useItemsWidth) &&
            (this.param.itemSpacing || this.param.itemsWidth)
        ) {
            node.control.li.width(this.param.itemSpacing || this.param.itemsWidth).addClass('specific-width');
        }

        // create li content
        this.createLicontent(node);

        // check text-align
        if (this._param.possibleRightAlign && this._param.align === null) {
            node.control.liContent.appendTo(this._body);
            if (node.control.liContent.css('text-align') == 'right') {
                node.control.liContent.addClass(rightTextAlign);
            }
            node.control.liContent.detach();
        }

        // align
        if (this._param.align !== null) {
            node.control.liContent.addClass(this._param.align + '-text-align');
        }

        // add li-content
        node.control.li.append(node.control.liContent);

        // childrens
        (!this.param.lazyLoadChildren || node.expanded) && this.addChildrens(node);

        // arrow
        this.addTreeArrow(node);

        // add mobile open icon
        // bind li content events
        this.bindLicontentEvent(node);

        // CM
        this.param.allowContextMenu && this.addContextMenu(node);

        // hover action
        !item.disable && this.itemHover(node);

        // bind drop and drag
        (node.param.level == 0 || node.parent.expanded) && this.param.canDrag && item.disableDrag !== true && this.bindDraggable(node);

        (node.param.level == 0 || node.parent.expanded) && this.param.canDrop && this.bindDroppable(node);

        if (this.param.allowSorting || item.allowSorting == true) {
            this.bindSortDraggable(node);
            this.bindSortDroppable(node);
        }

        this._onAppendItem(item, {
            addControl: function (control) {
                this._createControlInsideLi(node, control);
            }.bind(this)
        });

        // keyboard navigation
        //this.param.allowKeyBoardNavigation &&
        //this.keyNavigation(node.control.li);
        if (node.data.isSelected === true) {
            node.data.selected = true;
        }

        this.param.selectable && node.data.selected && node.control.select(true);

        this.param.checkable && node.data.checked && node.control.check(true);

        // do not expand dynamic item automaticaly
        (!item.dynamic || item.dinamicIsLoaded) && node.expanded && node.expand();
        return node;
    }

    _createControlInsideLi(node, control) {
        const div = document.createElement('div');
        div.className = 'li-control-container';
        const customControlContainer = $(div).appendTo(node.control.name).append(control);
    }

    protected _clickHandler(node, leftToRight, event?) {
        if (this.param.selectable && node.data.selectable !== false) {
            let value;

            if (event && event.ctrlKey) {
                value = node.selected === true ? false : true;
            } else {
                if (node.selected === undefined) {
                    value = true;
                } else {
                    value = this.param.unselectOnClick ? (node.selected === true ? false : true) : true;
                }
            }

            node.select(value, { event: event });
        }

        this._onItemClick(node.data, leftToRight, event);
    }

    protected _treeArrowClick(node) {}

    _setTabIndex(node) {
        this.param.allowKeyBoardNavigation && node.control.liContent.focus();
    }

    // eslint-disable-next-line complexity
    _keyEvent(e) {
        // key search
        if (this.param.allowKeySearch && e.which >= constants.keyCodes.KEY_A && e.which <= constants.keyCodes.KEY_Z) {
            const char = String.fromCharCode(e.which);
            if (char) {
                const node = this._treeStore.findNode(
                    function (data) {
                        return data[this.param.namePropertyName].toLowerCase().indexOf(char.toLowerCase()) == 0;
                    }.bind(this)
                );

                node && this.scrollToItem(node.data);
            }
        }

        // checkItems on Ctrl + A
        if (e.ctrlKey && e.which == constants.keyCodes.KEY_A) {
            const allItems = [];
            this._treeStore.forEachNode(function (i) {
                allItems.push(i.data);
            });

            if (allItems.length == this.selectedItems().length) {
                this.selectedItems([]);
            } else {
                this.selectedItems(allItems);
            }
        } else if (e.which == constants.keyCodes.DOWN_ARROW) {
            this.selectNextItem({ event: e });
        } else if (e.which == constants.keyCodes.LEFT_ARROW) {
            const selectedItem = this.selectedItems()[0] || null;
            if (selectedItem) {
                const n = this.findNode(function (i) {
                    return i == selectedItem;
                });
                n && n.collapse();
            }
        } else if (e.which == constants.keyCodes.RIGHT_ARROW) {
            const selectedItem = this.selectedItems()[0] || null;
            if (selectedItem) {
                const n = this.findNode(function (i) {
                    return i == selectedItem;
                });
                n && n.expand();
            }
        } else if (e.which == constants.keyCodes.UP_ARROW) {
            this.selectPrevItem({ event: e });
        } else if (e.which == constants.keyCodes.SPACE) {
            const selectedItems = this.selectedItems();
            const checkedItems = this.checkedItems();

            const allItems = [];
            this._treeStore.forEachNode(function (i) {
                allItems.push(i.data);
            });

            // if all selected items are checked
            if (
                !_(selectedItems).some(function (i) {
                    return !i.checked;
                })
            ) {
                this.checkedItems(_.difference(checkedItems, selectedItems));
            }

            // all items are checked
            else if (allItems.length == checkedItems.length) {
                this.checkedItems([]);
            } else {
                this.checkedItems(selectedItems.concat(checkedItems));
            }
        } else if (e.which == constants.keyCodes.ESCAPE) {
            this.selectedItems([]);
        } else if (e.which == constants.keyCodes.END) {
            const selectedItems = this.selectedItems();
            if (selectedItems.length == 1) {
                const node = this.findNode(function (i) {
                    return i == selectedItems[0];
                });
                if (node) {
                    if (node.expanded && node.hasChildren) {
                        this.scrollToItem(node.children[node.children.length - 1].data);
                    } else if (node.parent) {
                        this.scrollToItem(node.parent.children[node.parent.children.length - 1].data);
                    }
                }
            }
        } else if (e.which == constants.keyCodes.HOME) {
            const selectedItems = this.selectedItems();
            if (selectedItems.length == 1) {
                const node = this.findNode(function (i) {
                    return i == selectedItems[0];
                });
                if (node) {
                    if (node.expanded && node.hasChildren) {
                        this.scrollToItem(node.children[0].data);
                    } else if (node.parent) {
                        this.scrollToItem(node.parent.children[0].data);
                    }
                }
            }
        }
    }

    protected _setChildrenCoordinate(childrenUl, li, itemParameter) {
        if (!this.param.isVertical) {
            itemParameter.isRoot
                ? childrenUl.css({
                      top: li.offset().top + li.height(),
                      left: li.offset().left
                  })
                : childrenUl.css({
                      top: li.offset().top - 1,
                      left: li.offset().left + li.width() + 1
                  });
        } else {
            childrenUl.css({
                top: li.offset().top - 1,
                left: li.offset().left + li.width()
            });
        }
    }

    prependItems(items) {
        this._insertItems(items, { isPrepend: true });
    }

    appendItems(items) {
        this._insertItems(items, { isPrepend: false });
    }

    removeItems(compare) {
        this._treeStore.removeNodes(compare);

        if (this.param.lazyLoad) {
            setItems(this._items);
        }

        const length = compare && this._treeStore.items().length;
        if (this._emptyMessageBlock && length) {
            this._emptyMessageBlock.hide();
        } else if (this._emptyMessageBlock && !length) {
            this._emptyMessageBlock.show();
        }

        function setItems(items: any[]) {
            _.remove(items, compare).forEach(function (i) {
                if (i.children && i.children.length) {
                    setItems(i.children);
                }
            });
        }
    }

    updateItem(id, item) {
        this._treeStore.updateNode(id, item);
    }

    createName(node, imageCount) {
        const imageWidth = 20;
        const span = document.createElement('span');
        span.className = 'name';
        span.setAttribute('unselectable', 'on');
        node.control.name = $(span);

        if (node.data.control) {
            node.control.name.append(node.data.control);
        } else {
            node.control.name.text(node.data[this.param.namePropertyName]);
        }

        node.control.liContent.append(node.control.name);
    }

    createLicontent(node) {
        const item = node.data;

        const div = document.createElement('div');
        div.className = `li-content ${node.control.levelStyle.itemDefaultStyle} ${node.control.levelStyle.itemDefaultBorderStyle}`;
        div.setAttribute('tabindex', '-1');
        div.setAttribute('unselectable', 'on');
        div.setAttribute('title', node.data.title ? node.data.title : node.data[this.param.namePropertyName]);
        node.control.liContent = $(div);

        if (this.param.allowKeyBoardNavigation) {
            node.control.liContent.on('keydown', e => {
                this._keyEvent(e);
            });
        }

        // check
        if (this.param.checkable || node.data.checkable) {
            const checkDiv = document.createElement('div');
            checkDiv.className = 'checkbox';
            checkDiv.appendChild(document.createElement('span'));
            div.appendChild(checkDiv);
            node.control.checkbox = $(checkDiv);
        }

        let imageCount = 0;
        // create custom HTML in li content
        if (item.html) {
            node.control.liContent.append(item.html);
        }

        // create base li content
        else {
            // image
            imageCount = this.addImage(node);

            // create name
            if (item.control || item[this.param.namePropertyName]) {
                this.createName(node, imageCount);
            } else {
                div.classList.add('empty-name');
            }
        }

        // add custom css to li-content
        item.css && node.control.liContent.css(item.css);
        item.className && div.classList.add(item.className);
    }

    bindLicontentEvent(node) {
        const item = node.data;

        if (item.disable) {
            return;
        }

        // open node
        if (this.param.openByClick) {
            node.control.liContent.click(e => {
                this._setTabIndex(node);

                if (item.expandOnClick !== false && ((item.dynamic && !item.dinamicIsLoaded) || node.hasChildren) && !(e.ctrlKey || e.shiftKey)) {
                    if (this.param.lazyLoadChildren && !node.control.childrenUl) {
                        this.addChildrens(node);
                    }

                    this._treeArrowClick(node);
                }
            });
        }

        // click by li content
        node.control.liContent.click(e => {
            this._setTabIndex(node);
            this._clickHandler(node, null, e);
        });

        // click by check box
        if (this.param.checkable) {
            if (this.param.checkOnClick && !node.data.dynamic) {
                node.control.liContent.click(e => {
                    this._setTabIndex(node);

                    if (node.checkable() !== false) {
                        if (node.hasChildren && node.data.checkOnClick == true) {
                            node.check(node.checked === true ? false : true, { clientCheck: true });
                        } else if (!node.hasChildren && node.data.checkOnClick !== false) {
                            node.check(node.checked === true ? false : true, { clientCheck: true });
                        } else if (!node.hasChildren && node.data.checkOnClick == null) {
                            node.check(node.checked === true ? false : true, { clientCheck: true });
                        }
                    }
                });
            }

            node.control.checkbox.click(e => {
                this._setTabIndex(node);

                if (node.checkable() !== false) {
                    e.stopPropagation();

                    node.check(node.checked === true ? false : true, { clientCheck: true, event: e });

                    if (this.param.checkOnClick) {
                        node.select(node.checked, { event: e });
                    }
                }
            });
        }

        // double click
        node.control.liContent.doubleClick(() => {
            if (item.selectable !== false) {
                this._onItemDoubleClick(item);
            }
        });

        // right click
        node.control.liContent.on('contextmenu', () => {
            this._onItemRightClick(node.data);
        });
    }

    addContextMenu(node) {
        node.control.liContent.contextMenuAction(
            function (coordinates) {
                if (this.param.selectOnContextMenu) {
                    const selectedItems = this.selectedItems();

                    if (
                        selectedItems.length == 1 ||
                        !_(selectedItems).some(
                            function (i) {
                                return this._treeStore.compareItems(i, node.data);
                            }.bind(this)
                        )
                    ) {
                        node.select(true);
                    }
                }

                const menuItems = _.map(this.getContextMenuItems(node.data), function (menuItem) {
                    return {
                        item: menuItem,
                        checked: false
                    };
                });

                const contextMenu = ContextMenu(coordinates);
                contextMenu.setValue(menuItems);
                contextMenu.itemClick(
                    function (cmItem) {
                        this._onContextMenuItemClick(cmItem, node.data, node);
                    }.bind(this)
                );
            }.bind(this)
        );
    }

    itemHover(node) {
        let isSelected = false;

        node.control.liContent
            .on(
                pointerHelper.pointerOverEventName,
                function (e) {
                    if (hoverHelper.isEnter(e)) {
                        node.control && node.control.setHoverStyle();
                    }
                }.bind(this)
            )
            .on(
                pointerHelper.pointerOutEventName,
                function (e) {
                    if (hoverHelper.isLeave(e)) {
                        isSelected = node.data.selected || node.control.selected;

                        if (isSelected || node.control.liContent.hasClass('selected')) {
                            node.control.setSelectedStyle();
                        } else {
                            node.control.setDefaultStyle();
                        }
                    }
                }.bind(this)
            );
    }

    addImage(node) {
        const imageWidth = 20;
        const imageArray = [];

        if (node.data.image && node.data.icon) {
            node.control.liContent.addClass('multi-image');
        }

        if (node.data.image && !node.data.images) {
            const s = this.createImageSpan(urlHelper.getCssImageUrl(node.data.image));
            node.control.liContent.append(s);
            imageArray.push(s);
        } else if (node.data.images && node.data.images.length) {
            node.control.liContent.addClass('multi-image');

            _(node.data.images).forEach(i => {
                const s = this.createImageSpan(urlHelper.getCssImageUrl(i));
                node.control.liContent.append(s);
                imageArray.push(s);
            });
        }

        if (node.data.icon && !node.data.icons) {
            const s = this.createImageSpan('url("' + commonUtils.getImageSrc(node.data.icon) + '")');
            node.control.liContent.append(s);
            imageArray.push(s);
        } else if (node.data.icons && node.data.icons.length) {
            node.control.liContent.addClass('multi-image');

            _(node.data.icons).forEach(i => {
                const s = this.createImageSpan('url("' + commonUtils.getImageSrc(i) + '")');
                node.control.liContent.append(s);
                imageArray.push(s);
            });
        }

        if (this.param.imageServiceUrl) {
            const tmpImage = $(document.createElement('img'))
                .attr('src', this.param.imageServiceUrl(node.data[this.param.namePropertyName]))
                .on('load', () => {
                    !this.paramisTree &&
                        !this.param.isVertical &&
                        node.control.liContent.width(node.control.liContent.width() + this.param.iconWidth);

                    node.param.level == 0 ? this._rootUl.addClass('has-image') : node.parent.control.childrenUl.addClass('has-image');

                    node.control.liContent.prepend(this.createImageSpan('url("' + tmpImage.attr('src') + '")'));

                    tmpImage.remove();
                })
                .on('error', function () {});
        }

        if (node.data.image || node.data.icon || node.data.imageServiceUrl) {
            node.param.level == 0 ? this._rootUl.addClass('has-image') : node.parent.control.childrenUl.addClass('has-image');
        }

        return imageArray.length;
    }

    createImageSpan(image) {
        const span = document.createElement('span');
        span.className = 'image icon';
        span.style.backgroundImage = image;
        return span;
    }

    addTreeArrow(node) {}

    protected _loadDynamicItems(node) {}

    addChildrens(node) {
        const ol = document.createElement('ol');
        ol.className = `children ${this.param.contentStyle} ${this.param.defaultContentStyle}`;
        !this.param.isDesktop && ol.classList.add('mobile');
        node.control.childrenUl = $(ol);
        const fragment = document.createDocumentFragment();
        node.children &&
            node.children.forEach(i => {
                this._createItem(i);
                fragment.appendChild(i.control.li[0]);
            });
        ol.appendChild(fragment);
        node.control.childrenUl.appendTo(node.control.li);
    }

    showNextView(leftToRight) {
        const displayViewItems = _(this.getAllItems())
            .filter(function (i) {
                return i.executeAction == ExecuteAction.displayView;
            })
            .value();

        let newItem = null;

        if (displayViewItems.length) {
            if (this.currentItem == null) {
                newItem = displayViewItems[0];
            } else {
                _(displayViewItems).forEach(
                    function (i, index) {
                        if (i == this.currentItem) {
                            if (leftToRight) {
                                newItem = displayViewItems[(index > 0 ? index : displayViewItems.length) - 1];
                            } else {
                                newItem = displayViewItems[index < displayViewItems.length - 1 ? index + 1 : 0];
                            }

                            return false;
                        }
                    }.bind(this)
                );
            }

            if (newItem) {
                this.currentItem = newItem;

                const node = this.findNode(
                    function (data) {
                        return data == newItem;
                    }.bind(this)
                );

                this._clickHandler(node, leftToRight);
            }
        }
    }

    /* SelectNextItem/SelectPrevItem */
    selectNextItem(param?) {
        const n = this._treeStore.selectNextNode(param);
        this.ensureVisible(n);
    }

    selectPrevItem(param?) {
        const p = this._treeStore.selectPrevNode(param);
        this.ensureVisible(p);
    }

    /* Drag and Drop section */
    bindDraggable(node) {
        const control = node.control.liContent;
        control.dragBinded = true;
        const item = node.data;
        const levelStyle = node.control.levelStyle;

        this._dragAndDrop.drag(control, item);
        this._dragAndDrop.onDrag(
            function (data) {
                this._onDrag(data);
            }.bind(this)
        );
        this._dragAndDrop.onDragStart(
            function (data) {
                this._onDragStart(data);
                this._treeStore.forEachNode(
                    function (i) {
                        if (i.data.disableDrag !== true && i.control && i.control.dragBinded == true) {
                            if (this.param.canDrag) {
                                this._dragAndDrop.enableRefreshPosition(i.control.liContent);
                            }
                        }
                    }.bind(this)
                );
            }.bind(this)
        );

        this._dragAndDrop.onDragStop(
            function (data) {
                this._onDragStop(data);
                this._treeStore.forEachNode(
                    function (i) {
                        if (i.data.disableDrag !== true && i.control && i.control.dragBinded == true) {
                            if (this.param.canDrag) {
                                this._dragAndDrop.disableRefreshPosition(i.control.liContent);
                            }
                        }
                    }.bind(this)
                );
            }.bind(this)
        );
        this._dragAndDrop.onPreDrag(
            function (control, item) {
                this._onPreDrag();

                // drag array of items
                const currentDraggingItems = [];
                if (this.param.checkable) {
                    this._treeStore.forEachNode(function (i) {
                        i.checked && currentDraggingItems.push(i);
                    });
                }

                // if one from selected currently dragging
                else if (
                    _(this.selectedItems()).some(function (i) {
                        return i === item;
                    })
                ) {
                    this._treeStore.forEachNode(function (i) {
                        i.selected && currentDraggingItems.push(i);
                    });
                }

                const dataObjects = currentDraggingItems.length
                    ? _.map(currentDraggingItems, function (i) {
                          return i.data;
                      })
                    : [item];

                const uiObjects = currentDraggingItems.length
                    ? _.map(currentDraggingItems, function (i) {
                          return i.control.liContent
                              .clone()
                              .find('.checkbox.on')
                              .removeClass('on')
                              .addClass('hidden')
                              .end()
                              .removeClass(levelStyle.itemHoverStyle)
                              .removeClass(levelStyle.itemSelectedStyle)
                              .addClass(levelStyle.itemDefaultStyle)
                              .find('.li-control-container')
                              .remove()
                              .end();
                      })
                    : $(control);

                return {
                    dataObjects: dataObjects,
                    uiObjects: uiObjects
                };
            }.bind(this)
        );
    }

    bindSortDraggable(node) {
        const control = node.control.liContent;
        const item = node.data;
        const levelStyle = node.control.levelStyle;

        this._dragAndDropSort.drag(control, item);
    }

    bindSortDroppable(node) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const control = node.control.liContent;
        const item = node.data;
        let tmpLi;
        let timeout;
        let overTimeOut;
        let nodeFrom;
        let nodeTo;

        self._sortIsBefore;

        //this._dragAndDrop.drop(this._root);
        this._dragAndDropSort.drop(control, item);
        this._dragAndDropSort.onDragOut(function () {
            clearSortable();
        });
        this._dragAndDropSort.onDragStop(clearSortable);
        this._dragAndDropSort.onDragOver(function (data) {
            createTmpLi(data, true);
        });
        this._dragAndDropSort.onDragOut(function (data) {
            createTmpLi(data, false);
        });
        this._dragAndDropSort.onDropOver(function (data) {
            createTmpLi(data, true);
        });
        this._dragAndDropSort.onDrop(
            function (data) {
                afterDrop(data.event, data.uiObjects);
                self._onDrop(data);
            }.bind(this)
        );

        function createTmpLi(data, isOver) {
            data && data.dragContainer && data.dragContainer.hideDragDescription();

            clearSortable();
            if (data.objectFrom && data.objectTo) {
                nodeFrom = self._treeStore.findNode(function (d) {
                    return d == data.objectFrom;
                });
                nodeTo = self._treeStore.findNode(function (d) {
                    return d == data.objectTo;
                });

                if (isOver && data.objectTo.children && data.objectTo.children.length) {
                    timeout = setTimeout(
                        function () {
                            clearSortable();
                            nodeTo.expand();
                            self._treeStore.forEachNode(function (i) {
                                i.control.liContent.draggable('option', 'refreshPositions', true);
                            });
                        }.bind(self),
                        300
                    );
                } else if (self.param.allowNestedSorting && isOver && data.objectTo && data.objectTo.children && !data.objectTo.children.length) {
                    timeout = setTimeout(
                        function () {
                            clearSortable();
                            tmpLi = $('<li class="sorting-item"></li>').text(nodeFrom.data.name);
                            tmpLi.on(
                                'mouseup',
                                function (e) {
                                    nodeFrom.control.liContent.trigger('mouseup');
                                    nodeTo.control.liContent.trigger('mouseup');
                                    afterDrop(e, data.uiObjects);
                                    self._onDrop(data);
                                }.bind(self)
                            );

                            nodeTo.control.childrenUl.append(tmpLi);
                            nodeTo.control.li.addClass('opened');
                            self._treeStore.forEachNode(function (i) {
                                i.control.liContent.draggable('option', 'refreshPositions', true);
                            });
                        }.bind(self),
                        300
                    );
                }
                //else {
                const height = nodeTo.control.liContent.height();
                const position = pointerHelper.getPosition(data.event);
                self._sortIsBefore = nodeTo.control.liContent.offset().top + height / 2 > position.y;
                tmpLi = $('<li class="sorting-item"/>').text(nodeFrom.data.name);
                tmpLi.on(
                    'mouseup',
                    function (e) {
                        nodeFrom.control.liContent.trigger('mouseup');
                        nodeTo.control.liContent.trigger('mouseup');
                        afterDrop(e, data.uiObjects);
                        self._onDrop(data);
                    }.bind(self)
                );

                self._sortIsBefore ? nodeTo.control.li.before(tmpLi) : nodeTo.control.li.after(tmpLi);
                //}
            }
        }

        function clearSortable() {
            clearTimeout(timeout);
            clearTimeout(overTimeOut);
            timeout = null;
            tmpLi && tmpLi.remove();
        }

        function afterDrop(event, uiObjects) {
            // add to the empty folder
            if (nodeTo.data.children && nodeTo.data.children.length == 0 && nodeTo.control.childrenUl.find('.sorting-item').length) {
                self._onSort(nodeFrom.parent ? nodeFrom.parent.data : null, nodeTo.data, nodeFrom.data, -1);

                nodeFrom.remove();
                nodeTo.add(nodeFrom.data);
                uiObjects.remove();

                self._onSorted(nodeFrom.data);
            }

            //
            else {
                //if (!nodeTo.data.children || (nodeTo.data.children && !nodeTo.expanded)){
                const newItem = nodeFrom.data;
                const height = nodeTo.control.liContent.height();
                const insertBefore = self._sortIsBefore; // (nodeTo.control.liContent.offset().top + height/2 < event.clientY);

                let index;
                if (nodeTo.parent) {
                    _(nodeTo.parent.children).forEach(function (i, ind) {
                        if (i == nodeTo) {
                            index = ind;
                            return false;
                        }
                    });

                    self._onSort(
                        nodeFrom.parent ? nodeFrom.parent.data : null,
                        nodeTo.parent ? nodeTo.parent.data : null,
                        nodeFrom.data,
                        insertBefore ? index : index + 1
                    );

                    nodeFrom.remove();
                    _(nodeTo.parent.children).forEach(function (i, ind) {
                        if (i == nodeTo) {
                            index = ind;
                            return false;
                        }
                    });
                    nodeTo.parent.insert(insertBefore ? index : index + 1, newItem);
                    self._onSorted(newItem);
                } else {
                    _(self._treeStore.nodes()).forEach(function (i, ind) {
                        if (i == nodeTo) {
                            index = ind;
                            return false;
                        }
                    });

                    self._onSort(
                        nodeFrom.parent ? nodeFrom.parent.data : null,
                        nodeTo.parent ? nodeTo.parent.data : null,
                        nodeFrom.data,
                        insertBefore ? index : index + 1
                    );

                    nodeFrom.remove();

                    _(self._treeStore.nodes()).forEach(function (i, ind) {
                        if (i == nodeTo) {
                            index = ind;
                            return false;
                        }
                    });

                    self._treeStore.insert(insertBefore ? index : index + 1, newItem);
                    self._onSorted(newItem);
                }

                self._dragAndDrop.offDropOverFunction();
                clearSortable();
                uiObjects.remove();
            }
        }
    }

    bindDroppable(node) {
        const control = $('<div class="droppable-area">').appendTo(node.control.liContent);
        node.control.liContent.dropBinded = true;
        const item = node.data;

        this._dragAndDrop.drop(control, item);
        this._dragAndDrop.onDragOver(
            function (data) {
                this._onDragOver(data);
            }.bind(this)
        );
        this._dragAndDrop.onDragOut(
            function (data) {
                this._onDragOut(data);
            }.bind(this)
        );
        this._dragAndDrop.onDropOver(
            function (data) {
                this._onDropOver(data);
            }.bind(this)
        );
        this._dragAndDrop.onDrop(
            function (data) {
                this._onDrop(data);
                if (this._dropControls && this._dropControls.length) {
                    _(this._dropControls).forEach(
                        function (i) {
                            i.droppable('destroy');
                            this.bindDroppableControl(i, { isRebind: true });
                        }.bind(this)
                    );
                }
            }.bind(this)
        );
    }

    bindDroppableControl(control, param?) {
        if (!param || !param.isRebind) {
            this._dropControls.push(control);
        }

        this._dragAndDrop.drop(control);
        this._dragAndDrop.onDragOver(
            function (data) {
                this._onDragOver(data);
            }.bind(this)
        );
        this._dragAndDrop.onDragOut(
            function (data) {
                this._onDragOut(data);
            }.bind(this)
        );
        this._dragAndDrop.onDropOver(
            function (data) {
                this._onDropOver(data);
            }.bind(this)
        );
        this._dragAndDrop.onDrop(
            function (data) {
                this._onDrop(data);

                if (this._dropControls && this._dropControls.length) {
                    _(this._dropControls).forEach(
                        function (i) {
                            i.droppable('destroy');
                            this.bindDroppableControl(i, { isRebind: true });
                        }.bind(this)
                    );
                }
            }.bind(this)
        );
    }

    /* Add/Insert section */
    add(item, node?) {
        if (node) {
            return node.add(item);
        }

        const result = this._treeStore.add(item);
        this.adjust();
        return result;
    }

    insert(index, item, node?) {
        if (node) {
            return node.insert(index, item);
        }

        const result = this._treeStore.insert(index, item);
        this.adjust();
        return result;
    }

    /* Style section */
    getLevelStyle(level, levelStyles, defaultLevelStyle) {
        if (this._levelStyles[level]) {
            return this._levelStyles[level];
        }

        const levelStyle = {} as any;

        // default
        levelStyle.itemDefaultStyle = (levelStyles[level] && levelStyles[level].itemDefaultStyle) || defaultLevelStyle.itemDefaultStyle;
        levelStyle.itemDefaultBorderStyle =
            (levelStyles[level] && levelStyles[level].itemDefaultBorderStyle) || defaultLevelStyle.itemDefaultBorderStyle;

        // selected
        levelStyle.itemSelectedStyle = (levelStyles[level] && levelStyles[level].itemSelectedStyle) || defaultLevelStyle.itemSelectedStyle;
        levelStyle.itemSelectedBorderStyle =
            (levelStyles[level] && levelStyles[level].itemSelectedBorderStyle) || defaultLevelStyle.itemSelectedBorderStyle;

        // hover
        levelStyle.itemHoverStyle = (levelStyles[level] && levelStyles[level].itemHoverStyle) || defaultLevelStyle.itemHoverStyle;
        levelStyle.itemHoverBorderStyle = (levelStyles[level] && levelStyles[level].itemHoverBorderStyle) || defaultLevelStyle.itemHoverBorderStyle;

        // opened
        levelStyle.itemOpenedStyle = (levelStyles[level] && levelStyles[level].itemOpenedStyle) || defaultLevelStyle.itemOpenedStyle;
        levelStyle.itemOpenedBorderStyle =
            (levelStyles[level] && levelStyles[level].itemOpenedBorderStyle) || defaultLevelStyle.itemOpenedBorderStyle;

        this._levelStyles.push(levelStyle);

        return levelStyle;
    }

    addItemStyle(itemId, className) {
        const node = this._treeStore.findNode(function (data) {
            return data.id == itemId;
        });

        if (node && node.control) {
            node.control.liContent.addClass(className);
        }
    }

    removeItemStyle(itemId, className) {
        const node = this._treeStore.findNode(function (data) {
            return data.id == itemId;
        });

        if (node && node.control) {
            node.control.liContent.removeClass(className);
        }
    }

    /* Validate */
    validate() {
        const value = this.selectedItems();
        let state = true;
        if ($.isFunction(this.param.validator)) {
            state = this.param.validator(value);
        }

        this._onValidate(state);
    }

    /* Set section */
    findItemById(id) {
        return this._treeStore.findItem(
            function (data) {
                return data[this.param.idPropertyName] == id;
            }.bind(this)
        );
    }

    findItem(comparer) {
        return this._treeStore.findItem(comparer);
    }

    findItems(comparer) {
        return this._treeStore.findItems(comparer);
    }

    findNodeById(id) {
        return this._treeStore.findNode(function (data) {
            return data.id == id;
        });
    }

    findNode(comparer) {
        return this._treeStore.findNode(comparer);
    }

    nodes(nodes) {
        return this._treeStore.nodes(nodes);
    }

    items(items) {
        if (items === undefined) {
            return this.param.lazyLoad ? this._items : this._treeStore.items();
        }

        //var stamp = (new Date()).getTime();
        this._maxVisibleCount = 0;

        //TODO: Rework this shit
        this._checkedItemsForEmpty = [];

        this._items = items;

        if (this.param.lazyLoad && this.param.visibilityComparer && $.isFunction(this.param.visibilityComparer)) {
            this.setNodesVisibility();
            this.setCheckBoxVisibility();
        } else {
            this._showItems(items);
            this.setNodesVisibility();
            this.setCheckBoxVisibility();
        }

        if (this._emptyMessageBlock && this._treeStore.items().length) {
            this._emptyMessageBlock.hide();
        } else if (this._emptyMessageBlock && !this._treeStore.items().length) {
            this._emptyMessageBlock.show();
        }

        this.adjust(null, null);

        return items;
    }

    _showItems(items) {
        // model
        if (this.param.lazyLoad) {
            if (this.param.sortable) {
                this.param.sort ? this._items.sort(this._param.sort) : this._items.sort(this._sortItem(this._param.namePropertyName));
            }

            this._treeStore.items(_.take(items, this._maxVisibleCount || this.param.scrollStepPackage));
        } else {
            this._treeStore.items(items);
        }

        // view
        this._rootUl.empty();
        if (this._treeStore.nodes().length) {
            //window.items = 0;
            //var s = (new Date()).getTime();
            const fragment = document.createDocumentFragment();
            this._treeStore.nodes().forEach(i => {
                this._visibleItemsCount++;
                this._createItem(i);
                return fragment.appendChild(i.control.li[0]);
            });
            this._rootUl.append(fragment);
            //items.length && console.log(((new Date()).getTime() - s)/window.items);
        }
    }

    selectedItems(selectedItems, customComparer, ready) {
        return this._treeStore.selectedItems(selectedItems, customComparer, ready);
    }

    checkedItems(checkedItems?, customComparer?, ready?, checkParam?) {
        if (this.param.lazyLoad) {
            if (checkedItems != undefined) {
                this._treeStore.checkedItems(checkedItems, null, null, checkParam);
                this._lazyCheckedItems(checkedItems);
            } else {
                const c = this._treeStore.checkedItems();
                this._lazyCheckedItems(c);
                return this._lazyCheckedItems();
            }
        } else {
            return this._treeStore.checkedItems(checkedItems, customComparer, ready, checkParam);
        }
    }

    _lazyCheckedItems(checkedItems?) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const result = [];

        function check(items) {
            checkedItems.sort(self._treeStore._param.comparer);

            _(items).forEach(function (i) {
                i.checked = false;

                const e = self._treeStore._binaryIndexOf(checkedItems, i);

                if (e !== -1) {
                    i.checked = true;
                }
                i.children && check(i.children);
            });
        }

        function find(items) {
            _(items).forEach(
                function (i) {
                    i.checked === true && result.push(i);
                    i.children && i.children.length && find(i.children);
                }.bind(this)
            );
        }

        if (checkedItems !== undefined) {
            checkedItems = commonUtils.clone(checkedItems);
            check(this._items);
        } else {
            find(this._items);

            return (
                _(result)
                    //.Union(this._treeStore._currentCheckedItems)
                    .union(this._treeStore._initCheckedItems)
                    .uniqBy(function (item) {
                        return item.id;
                    })
                    .filter(function (item) {
                        return item.id != constants.noFilterId;
                    })
                    .value()
            );
        }
    }

    getItems() {
        return this._treeStore.items();
    }

    getAllItems() {
        const results = [];

        function find(items) {
            _(items).forEach(function (i) {
                results.push(i);
                if (i.children && i.children.length) {
                    find(i.children);
                }
            });
        }

        find(this.getItems());
        return results;
    }

    getSelectedItemsDescription() {
        return this.getItemsDescription(this._treeStore.selectedItems());
    }

    getCheckedItemsDescription() {
        return this.getItemsDescription(this._treeStore.checkedItems());
    }

    getItemsDescription(items) {
        const result = items.reduce((acc, current) => {
            const desc = current[this.param.namePropertyName];
            if (desc && desc !== constants.noFilterId) {
                acc.push(desc);
            }
            return acc;
        }, []);

        if (this._param.sortable) {
            result.sort();
        }

        return result.join(', ');
    }

    getRootULHeight() {
        return this._rootUl.outerHeight(true);
    }

    getContentHeight() {
        return this._root.outerHeight(true);
    }

    getRealContentHeight() {
        const padding = _.parseInt(this._root.css('padding-top')) + _.parseInt(this._root.css('padding-bottom'));
        return this._rootUl.outerHeight(true) + padding;
    }

    getItemHeight() {
        if (this._treeStore.nodes().length) {
            const node = this._treeStore.nodes()[0];
            return node.control.liContent ? node.control.liContent.outerHeight(true) : 0;
        }

        return 0;
    }

    getItemValue(value) {
        let displayedValue;

        if (value && value[this.param.namePropertyName] != null) {
            displayedValue = value[this.param.namePropertyName] != '' ? value[this.param.namePropertyName] : '&nbsp;';
        } else if (value && value[this.param.idPropertyName] != null) {
            displayedValue = value[this.param.idPropertyName] != '' ? value[this.param.idPropertyName] : '&nbsp;';
        } else {
            displayedValue = '&nbsp;';
        }

        return displayedValue;
    }

    getItemRect(itemId) {
        const node = _(this.nodes()).find(
            function (i) {
                const data = i.data;
                return data && data[this.param.idPropertyName] == itemId;
            }.bind(this)
        );

        if (node && node.control && node.control.li && !node.control.li.hasClass('hidden')) {
            return {
                x: node.control.li.position().left,
                y: node.control.li.position().top,
                width: node.control.li.outerWidth(true),
                height: node.control.li.outerHeight(true)
            };
        } else {
            return null;
        }
    }

    getItemsCount() {
        let count = 0;

        function getCount(items) {
            _(items).forEach(function (i) {
                count++;
                i.children && i.children.length && getCount(i.children);
            });
        }

        getCount(this._treeStore.items());
        return count;
    }

    empty() {
        this.items([]);
    }

    /* Key navigation */
    keyNavigation(li) {
        /*var self = this;

        li.find('.li-content:first').on('keydown', (function(e) {

            e.stopPropagation();

            var control = null;

            // down
            e.keyCode == 40 && self.selectNextItem();// (control = li.next());
            // up
            e.keyCode == 38 && (control = li.prev());
            // left
            e.keyCode == 37 && (li.removeClass('opened'));
            // right
            e.keyCode == 39 && (li.addClass('opened'));
            // home
            e.keyCode == 36 && (control = li.parents('ol:first').children('li:first'));
            // end
            e.keyCode == 35 && (control = li.parents('ol:first').children('li:last'));

            if (control) {

                control.attr('tabindex', this.tabIndex);
                control.focus();
                self._clickHandler(control.data('item'), {}, control.find('.li-content'));
            }
        }));*/
    }

    /* Set section */
    setStyles() {
        throw 'setStyles is not implemented in baseMenu';
    }

    setExcludeMode() {
        throw 'setExcludeMode is not implemented in baseMenu';
    }

    setNodesVisibility(compareItems, param) {
        if (this.param.visibilityComparer && $.isFunction(this.param.visibilityComparer)) {
            if (param && param.openChildren && !this._openedNodes) {
                this._openedNodes = this._treeStore.getOpenedNodes();
            } else if (param && !param.openChildren && param.searchDone) {
                const selectedItems = this._treeStore.selectedItems();

                if (selectedItems.length) {
                    this._openedNodes = [];
                }
                this._treeStore.expandedItems(this._openedNodes);
                this._openedNodes = null;

                if (selectedItems.length) {
                    this.ensureVisible(selectedItems[0]);
                }
            }

            if (this.param.lazyLoad) {
                const validItems = _(this._items)
                    .filter(
                        function (i) {
                            return this.param.visibilityComparer(i);
                        }.bind(this)
                    )
                    .value();

                // if all items are valid
                if (this._items.length == validItems.length) {
                    this._visibleItemsCount = 0;
                    this._foundItems = [];
                    this._showItems(this._items);
                } else {
                    this._visibleItemsCount = 0;
                    this._foundItems = validItems;
                    this._showItems(validItems);
                }
            } else {
                this._treeStore.setNodesVisibility(this._treeStore.nodes(), param);
            }

            this.adjust();
        }
    }

    getVisibleNodes() {
        const visibleNodes = [];
        this._treeStore.forEachNode(function (i) {
            i._visibility !== false && visibleNodes.push(i);
        });
        return visibleNodes;
    }

    setCheckBoxVisibility(compareItems?, param?) {
        if (this.param.checkBoxVisibilityComparer && $.isFunction(this.param.checkBoxVisibilityComparer)) {
            this._treeStore.setCheckBoxVisibility(compareItems, param);
        }
    }

    setLevelStyles(levelStyles, defaultStyles) {
        this._levelStyles = [];

        if (!levelStyles) {
            levelStyles = this.param.levelStyle;
        }
        if (!defaultStyles) {
            defaultStyles = this.param.defaultLevelStyle;
        }

        function changeStyle(items) {
            _(items).forEach(
                function (node) {
                    node.control.setLevelStyles(levelStyles, defaultStyles);

                    if (node.children && node.children.length) {
                        changeStyle(node.children);
                    }
                }.bind(this)
            );
        }

        changeStyle(this._treeStore.nodes());

        this.param.levelStyle = levelStyles;
        this.param.defaultLevelStyle = defaultStyles;
    }

    ensureVisible(item) {
        const node = this._treeStore.findNodeByItem(item);
        node && node.ensureVisible();
        this.scrollToItem(item);

        return node;
    }

    scrollToItem(item) {
        if (this._treeStore.items().length === 1) {
            return;
        }

        const node = this._treeStore.findNodeByItem(item);

        setTimeout(
            function () {
                node && this.scroll && this.scroll.ensureVisible(node.control.liContent, 4, 4, 500);
            }.bind(this),
            1
        );

        return node;
    }

    setScrollVisibility(visible) {
        this.scroll && this.scroll.setScrollbarsVisibility(visible);
    }

    setFocusForRoot() {
        if (this._treeStore.nodes().length) {
            this._setTabIndex(this._treeStore.nodes()[0]);
        }
    }

    adjust(width?, height?) {
        width && this._root.width(width);
        height && this._root.height(height);

        if (
            this._root.elementInDOM() &&
            (this.param.align == AlignType.right.toLowerCase() ||
                this.param.align == AlignType.center.toLowerCase() ||
                this.param.align == AlignType.justify.toLowerCase())
        ) {
            this._treeStore.forEachNode(
                function (i) {
                    if (i.level == 0) {
                        i.control.name.css({ display: 'inline' });
                        const liContentWidth = i.control.liContent.outerWidth(true);
                        // console.log(liContentWidth);
                        const w = i.control.name.width();
                        const commonWidth = _.sumBy(i.control.liContent.children('*').not('.name'), function (n) {
                            return $(n).outerWidth(true);
                        });

                        if (commonWidth + w > liContentWidth) {
                            i.control.name.width(liContentWidth - commonWidth);
                        } else {
                            i.control.name.width(w);

                            // right align
                            if (this.param.align == AlignType.right.toLowerCase()) {
                                i.control.liContent.css({ paddingLeft: liContentWidth - 10 - (commonWidth + w) + 'px' });
                            }

                            // center and justify
                            else if (this.param.align == AlignType.center.toLowerCase() || this.param.align == AlignType.justify.toLowerCase()) {
                                i.control.liContent.css({
                                    paddingLeft: (liContentWidth - 10 - (commonWidth + w)) / 2 + 'px'
                                });
                            }
                        }

                        // console.log(i.control.liContent.css('padding-left'));
                        i.control.name.css({ display: 'block' });
                    }
                }.bind(this)
            );
        }

        if (!this.param.isVertical && width) {
            this.horizontalAlign(width);
        }
    }

    horizontalAlign(width) {
        const childItems = $('>li', this._rootUl)
            .toArray()
            .map(function (e) {
                return $(e);
            });
        const childItemsWidth = childItems
            .map(function (e) {
                return e.outerWidth(true);
            })
            .reduce(function (prev, curr) {
                return prev + curr;
            }, 0);
        const freeSpace = width - childItemsWidth;
        switch (this.param.itemsAlign) {
            case AlignType.left:
                break;
            case AlignType.center:
                this._rootUl.css('margin-left', freeSpace / 2);
                break;
            case AlignType.right:
                this._rootUl.css('margin-left', freeSpace);
                break;
            case AlignType.justify:
                if (childItems.length > 1) {
                    const justify = freeSpace / (childItems.length - 1);
                    for (let i = 1; i < childItems.length; i++) {
                        $(childItems[i]).css('margin-left', justify);
                    }
                }
                break;
        }
    }

    destroy() {
        safeDestroys(this.scroll, this._treeStore, this._dragAndDrop, this._dragAndDropSort);
        super.destroy();
        destroyEvents(this);
    }

    setScrollPosition(data) {
        this.scroll && data && this.scroll.setScrollPosition(data);
    }

    getScrollPosition() {
        if (this.scroll) {
            return this.scroll.getScrollPosition();
        }
    }

    getDynamicItems: GetDynamicItemsCallback = _.noop;

    getContextMenuItems(p?): IContextMenuItem[] {
        return undefined;
    }

    /* Event section */
    onValidate(fn) {
        $.isFunction(fn) && (this._onValidate = fn);
    }

    onScrollMove(fn) {
        $.isFunction(fn) && (this._onScrollMove = fn);
    }

    onDrag(fn) {
        $.isFunction(fn) && (this._onDrag = fn);
    }

    onDragOver(fn) {
        $.isFunction(fn) && (this._onDragOver = fn);
    }

    onDragOut(fn) {
        $.isFunction(fn) && (this._onDragOut = fn);
    }

    onDropOver(fn) {
        $.isFunction(fn) && (this._onDropOver = fn);
    }

    onDrop(fn) {
        $.isFunction(fn) && (this._onDrop = fn);
    }

    onDragStart(fn) {
        $.isFunction(fn) && (this._onDragStart = fn);
    }

    onDragStop(fn) {
        $.isFunction(fn) && (this._onDragStop = fn);
    }

    onPreDrag(fn) {
        $.isFunction(fn) && (this._onPreDrag = fn);
    }

    onSort(fn) {
        $.isFunction(fn) && (this._onSort = fn);
    }

    onSorted(fn) {
        $.isFunction(fn) && (this._onSorted = fn);
    }

    onExpand(fn) {
        $.isFunction(fn) && (this._onExpand = fn);
    }

    beforeExpand(fn) {
        $.isFunction(fn) && (this._beforeExpand = fn);
    }

    onCollapse(fn) {
        $.isFunction(fn) && (this._onCollapse = fn);
    }

    onSelectionChanged(fn) {
        $.isFunction(fn) && (this._onSelectionChanged = fn);
    }

    onCheckedChanged(fn) {
        $.isFunction(fn) && (this._onCheckedChanged = fn);
    }

    onScrolledToEnd(fn) {
        $.isFunction(fn) && (this._onScrolledToEnd = fn);
    }

    onContextMenuItemClick(fn) {
        $.isFunction(fn) && (this._onContextMenuItemClick = fn);
    }

    onItemClick(fn) {
        $.isFunction(fn) && (this._onItemClick = fn);
    }

    onItemRightClick(fn) {
        $.isFunction(fn) && (this._onItemRightClick = fn);
    }

    onItemCheck(fn) {
        $.isFunction(fn) && (this._onItemCheck = fn);
    }

    onItemDoubleClick(fn) {
        $.isFunction(fn) && (this._onItemDoubleClick = fn);
    }

    refreshScroll(fn?) {
        if (this.scroll) {
            this.scroll.refresh();
        }
    }

    onAppendItem(fn) {
        $.isFunction(fn) && (this._onAppendItem = fn);
    }
}

export interface BaseMenu extends BaseControl {
    ensureVisible(item: any): IBaseMenuNode;

    items(items?: any[]): any[];

    nodes(nodes?: any[]): any[];

    selectedItems(items?: any[], comparer?, ready?): any[];

    findNode(comparer: (item) => boolean): IBaseMenuNode;

    removeItems(comparer: (item) => boolean);

    updateItem(id, item);

    setNodesVisibility(compare?: (item: any) => boolean, param?);

    selectPrevItem(param): void;

    selectNextItem(param): void;

    onItemClick(fn: (item: any) => void);

    onItemDoubleClick(fn: (item: any) => void);

    onCheckedChanged(fn: (a, b) => void);

    onExpand(fn: (data) => void);

    beforeExpand(fn: (node) => boolean);

    onCollapse(fn: (data) => void);

    getContextMenuItems: (p?) => IContextMenuItem[];

    onContextMenuItemClick(fn: (cmItem: IContextMenuItem, item, treeNode: IBaseMenuNode) => void);

    checkedItems: (checkedItems?, customComparer?, ready?, checkParam?) => any;

    getRealContentHeight(): number;

    getRootULHeight(): number;

    getContentHeight(): number;

    getItemHeight(): number;

    setLevelStyles(levelStyles, defaultStyles?);

    getItemsCount(): number;

    getItemRect(itemId: any): { x: number; y: number; width: number; height: number };

    destroy();

    refreshScroll(fn);
}

export type GetDynamicItemsCallback = ICallback2<any, ICallback<{ dynamicItems: any[]; openNode?: boolean; clearNode?: boolean }>>;
